/**
 * SMS Service for Player Notifications
 *
 * Uses Firebase Extensions (Twilio) to send SMS messages.
 * Messages are queued by writing to the 'sms' collection, which triggers
 * the Firebase Extension to send via Twilio.
 */

import { db } from './config';
import {
  collection,
  addDoc,
  doc,
  getDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  writeBatch
} from 'firebase/firestore';
import { buildScoringUrl } from '../utils/tokenUtils';
import { formatPhoneE164 } from '../utils/phoneUtils';

// SMS collection name (used by Firebase Twilio Extension)
const SMS_COLLECTION = 'sms';

/**
 * Queues an SMS message to be sent via Firebase Extension
 *
 * @param {object} params - SMS parameters
 * @param {string} params.to - Phone number (will be formatted to E.164)
 * @param {string} params.body - Message body
 * @param {string} [params.tournamentId] - Associated tournament ID
 * @param {string} [params.matchId] - Associated match ID
 * @param {string} [params.playerId] - Associated player identifier
 * @returns {Promise<{ success: boolean, messageId?: string, error?: string }>}
 */
export async function queueSms({ to, body, tournamentId, matchId, playerId }) {
  try {
    // Format phone to E.164
    const formattedPhone = formatPhoneE164(to);

    if (!formattedPhone) {
      return { success: false, error: 'Invalid phone number' };
    }

    const smsDoc = {
      to: formattedPhone,
      body,
      // Metadata for tracking
      tournamentId: tournamentId || null,
      matchId: matchId || null,
      playerId: playerId || null,
      // Status tracking
      status: 'pending',
      createdAt: serverTimestamp(),
      // These fields are updated by the Firebase Extension
      delivery: {
        state: 'PENDING',
        startTime: null,
        endTime: null,
        error: null
      }
    };

    const docRef = await addDoc(collection(db, SMS_COLLECTION), smsDoc);

    return { success: true, messageId: docRef.id };
  } catch (error) {
    console.error('Error queuing SMS:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Sends opt-in confirmation message to a player
 * This message is required by Twilio/carriers when a user opts into SMS notifications
 *
 * @param {object} params - Opt-in parameters
 * @param {string} params.playerPhone - Player's phone number
 * @param {string} params.playerName - Player's name
 * @param {string} params.tournamentId - Tournament ID (optional, for tracking)
 * @returns {Promise<{ success: boolean, messageId?: string, error?: string }>}
 */
export async function sendOptInConfirmation({ playerPhone, playerName, tournamentId }) {
  const message = `Pool Tournaments: You've opted in to receive SMS match notifications and scoring links for tournament play. Message frequency varies by tournament activity. Message and data rates may apply. Reply HELP for help, STOP to cancel. Support: support@pooltournaments.app`;

  return queueSms({
    to: playerPhone,
    body: message,
    tournamentId: tournamentId || null,
    matchId: null,
    playerId: playerName
  });
}

/**
 * Sends match ready notification to a player
 *
 * @param {object} params - Notification parameters
 * @param {string} params.tournamentId - Tournament ID
 * @param {string} params.tournamentName - Tournament name
 * @param {string} params.matchId - Match ID
 * @param {string} params.playerPhone - Player's phone number
 * @param {string} params.playerName - Player's name
 * @param {string} params.opponentName - Opponent's name
 * @param {string} params.token - Player's scoring token
 * @param {number} [params.tableNumber] - Assigned table number
 * @returns {Promise<{ success: boolean, messageId?: string, error?: string }>}
 */
export async function sendMatchReadyNotification({
  tournamentId,
  tournamentName,
  matchId,
  playerPhone,
  playerName,
  opponentName,
  token,
  tableNumber,
  instanceDate
}) {
  const scoringUrl = buildScoringUrl(token, tournamentId, undefined, instanceDate);

  let message = `${tournamentName}: Your match is ready!`;
  message += `\n\nYou vs ${opponentName}`;

  if (tableNumber) {
    message += `\nTable ${tableNumber}`;
  }

  message += `\n\nScore your match here:\n${scoringUrl}`;

  return queueSms({
    to: playerPhone,
    body: message,
    tournamentId,
    matchId,
    playerId: playerName
  });
}

/**
 * Sends score mismatch notification to a player
 *
 * @param {object} params - Notification parameters
 * @param {string} params.tournamentId - Tournament ID
 * @param {string} params.matchId - Match ID
 * @param {string} params.playerPhone - Player's phone number
 * @param {string} params.playerName - Player's name
 * @param {string} params.token - Player's scoring token
 * @param {number} params.attemptNumber - Current attempt number (1-3)
 * @returns {Promise<{ success: boolean, messageId?: string, error?: string }>}
 */
export async function sendScoreMismatchNotification({
  tournamentId,
  matchId,
  playerPhone,
  playerName,
  token,
  attemptNumber,
  instanceDate
}) {
  const scoringUrl = buildScoringUrl(token, tournamentId, undefined, instanceDate);

  let message = `Score mismatch! Your submitted score didn't match your opponent's.`;
  message += `\n\nPlease re-enter your score (Attempt ${attemptNumber} of 3):`;
  message += `\n${scoringUrl}`;

  if (attemptNumber === 3) {
    message += `\n\nThis is your last attempt. If scores don't match, the tournament director will be notified.`;
  }

  return queueSms({
    to: playerPhone,
    body: message,
    tournamentId,
    matchId,
    playerId: playerName
  });
}

/**
 * Sends match finalized notification to a player
 *
 * @param {object} params - Notification parameters
 * @param {string} params.tournamentId - Tournament ID
 * @param {string} params.matchId - Match ID
 * @param {string} params.playerPhone - Player's phone number
 * @param {string} params.playerName - Player's name
 * @param {boolean} params.isWinner - Whether this player won
 * @param {object} params.score - Final score { p1: number, p2: number }
 * @returns {Promise<{ success: boolean, messageId?: string, error?: string }>}
 */
export async function sendMatchFinalizedNotification({
  tournamentId,
  matchId,
  playerPhone,
  playerName,
  isWinner,
  score
}) {
  let message;

  if (isWinner) {
    message = `Match complete! You won ${score.p1}-${score.p2}. Good luck in your next match!`;
  } else {
    message = `Match complete. Final score: ${score.p1}-${score.p2}. Thanks for playing!`;
  }

  return queueSms({
    to: playerPhone,
    body: message,
    tournamentId,
    matchId,
    playerId: playerName
  });
}

/**
 * Gets SMS status for a message
 *
 * @param {string} messageId - SMS document ID
 * @returns {Promise<{ status: string, delivery?: object, error?: string }|null>}
 */
export async function getSmsStatus(messageId) {
  try {
    const docRef = doc(db, SMS_COLLECTION, messageId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      status: data.status,
      delivery: data.delivery,
      createdAt: data.createdAt
    };
  } catch (error) {
    console.error('Error getting SMS status:', error);
    return { status: 'error', error: error.message };
  }
}

/**
 * Gets SMS history for a match
 *
 * @param {string} tournamentId - Tournament ID
 * @param {string} matchId - Match ID
 * @returns {Promise<Array>}
 */
export async function getMatchSmsHistory(tournamentId, matchId) {
  try {
    const q = query(
      collection(db, SMS_COLLECTION),
      where('tournamentId', '==', tournamentId),
      where('matchId', '==', matchId),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting match SMS history:', error);
    return [];
  }
}

/**
 * Checks rate limiting for SMS sends to prevent abuse
 * Limits: 10 SMS per tournament per hour
 *
 * @param {string} tournamentId - Tournament ID
 * @returns {Promise<{ allowed: boolean, remaining: number, resetAt?: Date }>}
 */
export async function checkSmsRateLimit(tournamentId) {
  try {
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

    const q = query(
      collection(db, SMS_COLLECTION),
      where('tournamentId', '==', tournamentId),
      where('createdAt', '>=', oneHourAgo),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const count = snapshot.size;
    const limit = 50; // 50 SMS per tournament per hour

    if (count >= limit) {
      // Find when the oldest message in the window will expire
      const oldestDoc = snapshot.docs[snapshot.docs.length - 1];
      const oldestTime = oldestDoc.data().createdAt?.toDate() || new Date();
      const resetAt = new Date(oldestTime.getTime() + 60 * 60 * 1000);

      return {
        allowed: false,
        remaining: 0,
        resetAt
      };
    }

    return {
      allowed: true,
      remaining: limit - count
    };
  } catch (error) {
    console.error('Error checking SMS rate limit:', error);
    // Allow on error to not block functionality
    return { allowed: true, remaining: 50 };
  }
}

/**
 * Sends "next match ready" notifications to players whose match just became ready
 * Called after a match is finalized to notify players in subsequent matches
 *
 * @param {object} params - Notification parameters
 * @param {string} params.tournamentId - Tournament ID
 * @param {string} params.tournamentName - Tournament name
 * @param {Array} params.bracket - Current bracket array
 * @param {object} params.playerPhones - Map of player names to phone numbers
 * @param {number} [params.finishedMatchIndex] - Index of match that just finished (to find affected matches)
 * @returns {Promise<{ success: boolean, notified: number, errors: string[] }>}
 */
export async function sendNextMatchReadyNotifications({
  tournamentId,
  tournamentName,
  bracket,
  playerPhones,
  instanceDate
}) {
  const errors = [];
  let notified = 0;

  try {
    // Check rate limit first
    const rateLimit = await checkSmsRateLimit(tournamentId);
    if (!rateLimit.allowed) {
      return { success: false, notified: 0, errors: ['Rate limit exceeded'] };
    }

    // Find matches that are ready (both players determined, not finished, not notified)
    for (let i = 0; i < bracket.length; i++) {
      const match = bracket[i];

      // Skip if match is already finished or already started
      if (match.finished || match.started) continue;

      // Skip if already notified for this match
      if (match.nextMatchNotified) continue;

      // Get player names
      const player1Name = match.p1?.name || match.player1;
      const player2Name = match.p2?.name || match.player2;

      // Skip if either player is not determined yet
      if (!player1Name || !player2Name) continue;
      if (player1Name === 'BYE' || player2Name === 'BYE') continue;
      if (player1Name.includes('Winner of') || player2Name.includes('Winner of')) continue;
      if (player1Name.includes('Loser of') || player2Name.includes('Loser of')) continue;

      // Match is ready! Send notifications to both players
      const playersToNotify = [
        { name: player1Name, position: 'p1', token: match.p1?.scoringToken },
        { name: player2Name, position: 'p2', token: match.p2?.scoringToken }
      ];

      for (const player of playersToNotify) {
        const phone = playerPhones?.[player.name];
        if (!phone || !player.token) {
          continue; // Skip if no phone or token
        }

        try {
          const result = await sendMatchReadyNotification({
            tournamentId,
            tournamentName,
            matchId: `match_${i}`,
            playerPhone: phone,
            playerName: player.name,
            opponentName: player.position === 'p1' ? player2Name : player1Name,
            token: player.token,
            tableNumber: match.table,
            instanceDate
          });

          if (result.success) {
            notified++;
          } else {
            errors.push(`Failed to notify ${player.name}: ${result.error}`);
          }
        } catch (err) {
          errors.push(`Error notifying ${player.name}: ${err.message}`);
        }
      }
    }

    return { success: true, notified, errors };
  } catch (error) {
    console.error('Error sending next match notifications:', error);
    return { success: false, notified, errors: [...errors, error.message] };
  }
}

/**
 * Deletes all SMS documents for a tournament
 * Called when a tournament is reset to clean up old SMS records
 *
 * @param {string} tournamentId - Tournament ID
 * @returns {Promise<{ success: boolean, deleted: number, error?: string }>}
 */
export async function deleteTournamentSmsDocuments(tournamentId) {
  try {
    if (!tournamentId) {
      return { success: false, deleted: 0, error: 'Tournament ID required' };
    }

    // Query all SMS documents for this tournament
    const q = query(
      collection(db, SMS_COLLECTION),
      where('tournamentId', '==', tournamentId)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return { success: true, deleted: 0 };
    }

    // Delete in batches (Firestore batch limit is 500)
    const batchSize = 500;
    let deleted = 0;

    const docs = snapshot.docs;
    for (let i = 0; i < docs.length; i += batchSize) {
      const batch = writeBatch(db);
      const batchDocs = docs.slice(i, i + batchSize);

      batchDocs.forEach(docSnapshot => {
        batch.delete(docSnapshot.ref);
      });

      await batch.commit();
      deleted += batchDocs.length;
    }

    return { success: true, deleted };
  } catch (error) {
    console.error('Error deleting tournament SMS documents:', error);
    return { success: false, deleted: 0, error: error.message };
  }
}
